<template>
    <div>
        <ForceNewsBlockWrapper :requireCharacters="requireCharacters"
                               :titleLabel="$t('profile.label.password')"
                               :parentScreen="parentScreen">
            <div class="form-group row">
                <label class="control-label col-md-3 col-form-label">{{$t('profile.label.current_password')}}</label>
                <div class="col-md-6">
                    <input name="currentPassword"
                           id="currentPassword"
                           type="password"
                           class="form-control"
                           v-model="$v.input['amsCustomerDetail.currentPassword'].$model"
                           maxlength="64"
                           @keyup="handleForceNewEvent"
                           @blur="checkPassword($v.input['amsCustomerDetail.newPassword'].$model)"
                           v-remove-fullwidth-character
                           v-keep-only-character-pwd
                    >
                    <error-messages-block :field="$v.input['amsCustomerDetail.currentPassword']">
                        <template #required>
                            {{$t('MSG_TRS_NAF_0004_param',{param1 : $t('profile.label.current_password')})}}
                        </template>
                        <template #passwordTrimValidation>
                            {{$t('MSG_TRS_NAF_0004_param',{param1 : $t('profile.label.current_password')})}}
                        </template>
                    </error-messages-block>

                </div>
            </div>

            <div class="form-group row">
                <label class="control-label col-md-3 col-form-label">{{$t('profile.label.new_password')}}</label>
                <div class="col-md-6">
                    <input type="password"
                           v-model="$v.input['amsCustomerDetail.newPassword'].$model"
                           name="password"
                           class="form-control"
                           maxlength="64"
                           v-password-strength
                           data-id="password"
                           :placeholder="$t('password.message.input_pass9')"
                           @keyup="handleForceNewEvent"
                           v-remove-fullwidth-character
                           v-keep-only-character-pwd
                    >
                    <error-messages-block :field="$v.input['amsCustomerDetail.newPassword']">
                        <template #required>
                            {{ $t('MSG_TRS_NAF_0004_param', {param1: $t('profile.label.new_password')}) }}
                        </template>
                        <template #regexValidatePassword>
                            {{ $t('MSG_TRS_NAF_0197') }}
                        </template>
                        <template #validatePassword>
                            {{ $t('MSG_TRS_NAF_0197') }}
                        </template>
                    </error-messages-block>
                </div>
            </div>

            <div class="form-group row">
                <label class="control-label col-md-3 col-form-label">{{$t('profile.label.retype_password')}}</label>
                <div class="col-md-6">
                    <input type="password"
                           class="form-control"
                           v-model="$v.input['amsCustomerDetail.reTypePassword'].$model"
                           name="reTypePassword"
                           maxlength="64"
                           :placeholder="$t('password.message.input_pass10')"
                           @keyup="handleForceNewEvent"
                           v-remove-fullwidth-character
                           v-keep-only-character-pwd
                    >
                    <error-messages-block :field="$v.input['amsCustomerDetail.reTypePassword']">
                        <template #requiredIf>
                            {{$t('MSG_TRS_NAF_0004_param',{param1 : $t('profile.label.retype_password')})}}
                        </template>
                        <template #sameAsPassword>
                            {{$t('profile.error.password.not_match')}}
                        </template>
                    </error-messages-block>
                </div>
            </div>

            <div class="form-group row">
                <div class="col-md-3"></div>
                <div class="password-message col-md-6">
                    <div v-html="$t('password.message.input_pass1')"></div>
                    <div v-html="$t('password.message.input_pass2')"></div>
                    <div v-html="$t('password.message.input_pass3')"></div>
                    <div v-html="$t('password.message.input_pass4')"></div>
                    <div v-html="$t('password.message.input_pass5')"></div>
                    <div v-html="$t('password.message.input_pass6', {supportSecurityUrl: headerInfo.supportSecurityUrl})"></div>
                </div>
            </div>

            <template v-if="this.isParentScreenEditProfile">
                <div class="form-action">
                    <button class="btn btn-sm btn-submit btn-success mr-2"
                            :disabled="isDisabledButtonSubmit"
                            :type="this.getButtonType()">
                        {{$t('profile.button.submit')}}
                    </button>
                    <button class="btn btn-default btn-cancel btn-sm"
                            type="button"
                            @click="reverseInput">
                        {{$t('profile.button.cancel')}}
                    </button>
                </div>
            </template>
        </ForceNewsBlockWrapper>
    </div>
</template>

<script>
/**
 * @author HungNS
 */
import formMixin from '@/common/mixins/form';
import forceNewUtil from "@/common/mixins/forceNewUtil";
import PasswordMixins from "@/common/mixins/PasswordMixins";
import {
    passwordTrimValidation,
    regexValidatePassword
} from '@/common/helpers/validation'
import {required, sameAs, helpers, requiredIf} from 'vuelidate/lib/validators';
import IConstants from "../../../../common/constants/IConstants";
import {isEmpty} from "lodash";
import {createNamespacedHelpers} from 'vuex';

const {mapState: mapHeaderState} = createNamespacedHelpers("header");
const {mapState: mapForceNewsState} = createNamespacedHelpers('forceNews');

export default {
    name: "UpdatePwd",
    props: {
        displayForms: {
            type: String,
            required: false,
            default: ''
        },
        storeState: {
            type: Object,
        },
        maskingVerifiedPerSession: {
            type: Boolean
        }
    },
    mixins: [formMixin, forceNewUtil, PasswordMixins],
    computed: {
        ...mapHeaderState({
            headerInfo: state => state.headerInfo,
        }),
        ...mapForceNewsState({
            customerInfoView: state => state.customerInfoView
        }),
        // why put it as computed: It is not possible to dynamically pass parameters to Vue mixins, so we work around
        displayFormCode() {
            return IConstants.FORCE_NEW_DISPLAY_FORM_CODE.PASSWORD;
        },
        isDisabledButtonSubmit() {
            const {$v} = this;
            const masterInput = this.masterInput;
            const hasNotChangeInput = masterInput && masterInput === JSON.stringify(this.input);
            return $v.$anyError || hasNotChangeInput || !this.input['amsCustomerDetail.reTypePassword']
                || !this.input['amsCustomerDetail.currentPassword'];
        },
    },
    methods: {
        checkPassword(newPassword) {
            if (isEmpty(newPassword)) {
                this.$v.input['amsCustomerDetail.newPassword'].$reset();
            }
        },
        sameAsPasswordIfExist() {
            let newPassword = this.input['amsCustomerDetail.newPassword'];
            let retypePassword = this.input['amsCustomerDetail.reTypePassword'];
            if (!helpers.req(newPassword) || !helpers.req(retypePassword)) {
                return true;
            } else {
                return newPassword === retypePassword;
            }
        },
        validatePassword() {
            let newPassword = this.input['amsCustomerDetail.newPassword'];
            let email = this.input['amsCustomerDetail.email'];
            let additionalEmail = this.input['amsCustomerDetail.additionalEmail'];
            let corporationType = this.input['amsCustomerDetail.corporationType'];
            let phone = this.input['amsCustomerDetail.phone'];
            let phone2 = this.input['amsCustomerDetail.phone2'];
            let birthday = this.input['amsCustomerDetail.birthday'];
            let picPhone = this.input['amsCustomerDetail.picPhone'];
            let establishDate = this.input['amsCustomerDetail.establishDate'];
            const MIN_LENGTH = 10;
            const MAX_LENGTH = 64;

            if (newPassword.length < MIN_LENGTH || newPassword.length > MAX_LENGTH) {
                return false;
            }

            if (!regexValidatePassword(newPassword)) {
                return false;
            }
            
            if (this.parentScreen === IConstants.PARENT_SCREEN.FORCE_NEWS) {
                if (!isEmpty(email) && this.checkPasswordRelatedInformation(email, newPassword)) {
                    return false;
                }

                if (!isEmpty(additionalEmail) && this.checkPasswordRelatedInformation(additionalEmail, newPassword)) {
                    return false;
                }

                if (!isEmpty(phone) && this.checkPasswordRelatedInformation(phone, newPassword)) {
                    return false;
                }

                if (corporationType === this.IConstants.CUSTOMER_TYPE.INDIVIDUAL) {
                    if (!isEmpty(phone2) && this.checkPasswordRelatedInformation(phone2, newPassword)) {
                        return false;
                    }

                    if (!isEmpty(birthday) && this.checkPasswordRelatedInformation(birthday, newPassword)) {
                        return false;
                    }
                } else {
                    if (!isEmpty(picPhone) && this.checkPasswordRelatedInformation(picPhone, newPassword)) {
                        return false;
                    }

                    if (!isEmpty(establishDate) && this.checkPasswordRelatedInformation(establishDate, newPassword)) {
                        return false;
                    }
                }
            } else {
                if (this.maskingVerifiedPerSession) {
                    if (!isEmpty(email) && this.checkPasswordRelatedInformation(email, newPassword)) {
                        return false;
                    }

                    if (!isEmpty(additionalEmail) && this.checkPasswordRelatedInformation(additionalEmail, newPassword)) {
                        return false;
                    }

                    if (!isEmpty(phone) && this.checkPasswordRelatedInformation(phone, newPassword)) {
                        return false;
                    }

                    if (corporationType === this.IConstants.CUSTOMER_TYPE.INDIVIDUAL) {
                        if (!isEmpty(phone2) && this.checkPasswordRelatedInformation(phone2, newPassword)) {
                            return false;
                        }

                        if (!isEmpty(birthday) && this.checkPasswordRelatedInformation(birthday, newPassword)) {
                            return false;
                        }
                    } else {
                        if (!isEmpty(picPhone) && this.checkPasswordRelatedInformation(picPhone, newPassword)) {
                            return false;
                        }

                        if (!isEmpty(establishDate) && this.checkPasswordRelatedInformation(establishDate, newPassword)) {
                            return false;
                        }
                    }
                }
            }
            
            return true;
        },
    },
    validations() {
        if (this.isForceNewTypeOn) {
            return {
                input: {
                    'amsCustomerDetail.currentPassword': {
                        required,
                        passwordTrimValidation,
                    },
                    'amsCustomerDetail.newPassword': {
                      required,
                      regexValidatePassword,
                        validatePassword: () => {
                            return this.validatePassword();
                        }
                    },
                    'amsCustomerDetail.reTypePassword': {
                      required,
                      sameAsPassword: this.sameAsPasswordIfExist
                    },
                },
            }
        } else {
            return {
                input: {
                    'amsCustomerDetail.currentPassword': {
                        required,
                    },
                    'amsCustomerDetail.newPassword': {
                        required,
                        regexValidatePassword,
                        validatePassword: () => {
                            return this.validatePassword();
                        }
                    },
                    'amsCustomerDetail.reTypePassword': {
                        requiredIf: requiredIf(function () {
                            return this.input['amsCustomerDetail.reTypePassword'] !== null;
                        }),
                        sameAsPassword: sameAs("amsCustomerDetail.newPassword")
                    },
                },
            }
        }
    },
}
</script>

<style scoped>
::placeholder {
    opacity: 0.7;
}
</style>
